import { Fragment, useEffect, useState } from 'react';

import crc16modbus from 'crc/crc16modbus';

import AutorenewIcon from '@mui/icons-material/Autorenew';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Slider from '@mui/material/Slider';
import Switch from '@mui/material/Switch';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import ToggleButton from '@mui/material/ToggleButton';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import PinIcon from '@mui/icons-material/Pin';
import StraightenIcon from '@mui/icons-material/Straighten';
import OfflineBoltIcon from '@mui/icons-material/OfflineBolt';

import Title from './Title';

export default function Settings(props) {
    const { btBusy, changeSetting, data, device, setData, interval, polling, setPolling, properties, sendProperties, setRawData } = props

    const [ sliderState, setSliderState ] = useState({
        speedLimit: data?.speedLimit?.value || 25,
        supportLevel: data?.supportLevel || 0
    })

    useEffect(() => {
        if (data.speedLimit !== undefined && data.supportLevel !== undefined ) {
            console.log("data changed!!!")
            if (sliderState.speedLimit !== data.speedLimit.value && sliderState.supportLevel !== data.supportLevel) {
                setSliderState({
                    speedLimit: data.speedLimit.value,
                    supportLevel: data.supportLevel
                })
            } else if (sliderState.supportLevel !== data.supportLevel) {
                setSliderState((prevState) => {
                    return {
                        ...prevState,
                        supportLevel: data.supportLevel
                    }
                })
            } else if (sliderState.speedLimit !== data.speedLimit) {
                setSliderState((prevState) => {
                    return {
                        ...prevState,
                        speedLimit: data.speedLimit.value
                    }
                })
            }
        }
    }, [data])

    const handleSlider = (propertyName, value) => {
        interval.current && clearInterval(interval.current)

        if (propertyName === "speedLimit") {
            setSliderState((prevState) => {
                return {
                    ...prevState,
                    speedLimit: value
                }
            })
        } else {
            setSliderState((prevState) => {
                return {
                    ...prevState,
                    supportLevel: value
                }
            })
        }
    }

    const handleToggle = ( propertyName, value ) => {
        const toggle = () => {
            const property = properties.find(propertyObj => propertyObj.name === propertyName).number
            if (propertyName === ("light")) {
                changeSetting(property, 1, data[propertyName] ? [0] : [1])
                setData((prevState) => {
                    return {
                        ...prevState,
                        [propertyName]: ! prevState[propertyName]
                    }
                })
            } else if (propertyName === ("unit")) {
                if (value !== null) {
                    changeSetting(property, 1, [value])
                    setData((prevState) => {
                        return {
                            ...prevState,
                            [propertyName]: value
                        }
                    })   
                }
            } else if (propertyName === ("speedLimit")) {
                    changeSetting(property, 2, [value, 0])   
                    setData((prevState) => {
                        return {
                            ...prevState,
                            [propertyName]: {
                                bool: prevState[propertyName].bool,
                                value: value
                            }
                        }
                    }) 
            } else {
                changeSetting(property, 1, [value])   
                setData((prevState) => {
                    return {
                        ...prevState,
                        [propertyName]: value
                    }
                })    
            }
            if (polling) {
                interval.current = setInterval(() => {
                    ! btBusy && sendProperties()
                }, 2000)
            }
        }

        interval.current && clearInterval(interval.current)

        if (btBusy) {
            setTimeout(toggle, 1000)
        } else {
            toggle()
        }
    }

    return (
        <Fragment>
            <Title>Settings</Title>
            <List
                sx={{ 
                    width: '100%', 
                    display: {
                        lg: "flex",
                    },
                    gap: {
                        lg: "1.5em"
                    } 
                }}
            >
                <ListItem sx={{flexBasis: "15%"}}>
                    <ListItemIcon sx={{minWidth: "2rem"}}>
                        <AutorenewIcon />
                    </ListItemIcon>
                    <ListItemText id="switch-list-label-interval" primary="Refresh" />
                    <Switch
                        edge="end"
                        onChange={(event) => {
                            if ( polling ) {
                                console.log("clearing interval ...")
                                clearInterval(interval.current)
                                setPolling(false)
                            } else {
                                console.log("setting interval ...")
                                interval.current = setInterval(() => {
                                    sendProperties()
                                }, 2000)
                                setPolling(true)
                            }
                        }}
                        checked={polling}
                        value="interval"
                        inputProps={{
                            'aria-labelledby': 'switch-list-label-interval',
                        }}
                    />
                </ListItem>
                <ListItem sx={{flexBasis: "15%"}}>
                    <ListItemIcon sx={{minWidth: "2rem"}}>
                        <LightbulbIcon />
                    </ListItemIcon>
                    <ListItemText id="switch-list-label-front-light" primary="Light" />
                    <Switch
                        disabled={! device?.gatt?.connected || data.light === undefined}
                        edge="end"
                        onChange={(event) => handleToggle("light")}
                        checked={data?.light || false}
                        value="light"
                        inputProps={{
                            'aria-labelledby': 'switch-list-label-front-light',
                        }}
                    />
                </ListItem>
                <ListItem sx={{flexWrap: "wrap", flexBasis: "25%"}}>
                    <ListItemIcon sx={{minWidth: "2rem"}}>
                        <PinIcon />
                    </ListItemIcon>
                    <ListItemText id="switch-list-label-support-level" primary="Support Level (PAS)" />
                    <Slider
                        disabled={! device?.gatt?.connected || data.supportLevel === undefined}
                        value={sliderState.supportLevel}
                        onChange={(event, value) => handleSlider("supportLevel", value)}
                        onChangeCommitted={(event, value) => handleToggle("supportLevel", value)}
                        getAriaValueText={value => value}
                        valueLabelDisplay="auto"
                        step={1}
                        marks
                        min={0}
                        max={3}
                        inputProps={{
                            'aria-labelledby': 'switch-list-label-speed-limit',
                        }}
                        sx={{flexBasis: "100%"}}
                    />
                </ListItem>
                <ListItem sx={{flexWrap: "wrap", flexBasis: "25%"}}>
                    <ListItemIcon sx={{minWidth: "2rem"}}>
                        <OfflineBoltIcon />
                    </ListItemIcon>
                    <ListItemText id="switch-list-label-speed-limit" primary="Speed Limit" />
                    <Slider
                        disabled={! device?.gatt?.connected || data?.speedLimit?.bool === true || data?.speedLimit?.value === undefined}
                        value={sliderState.speedLimit}
                        onChange={(event, value) => handleSlider("speedLimit", value)}
                        onChangeCommitted={(event, value) => handleToggle("speedLimit", value)}
                        getAriaValueText={value => value}
                        valueLabelDisplay="auto"
                        step={1}
                        marks
                        min={20}
                        max={37}
                        inputProps={{
                            'aria-labelledby': 'switch-list-label-speed-limit',
                        }}
                        sx={{flexBasis: "100%"}}
                    />
                </ListItem>
                <ListItem sx={{flexWrap: "wrap", flexBasis: "20%"}}>
                    <ListItemIcon sx={{minWidth: "2rem"}}>
                        <StraightenIcon />
                    </ListItemIcon>
                    <ListItemText id="switch-list-label-unit" primary="Unit" />
                    <ToggleButtonGroup
                        onChange={(event, value) => handleToggle("unit", value)}
                        value={data.unit}
                        color="primary"
                        exclusive
                        inputProps={{
                            'aria-labelledby': 'switch-list-label-unit',
                        }}
                    >
                        <ToggleButton disabled={! device?.gatt?.connected || data.unit === undefined} value={false}>km</ToggleButton>
                        <ToggleButton disabled={! device?.gatt?.connected || data.unit === undefined} value={true}>mi</ToggleButton>
                    </ToggleButtonGroup>
                </ListItem>
            </List>
        </Fragment>
    );
}